package hk.com.vr360_demo2.webapp.component.editor_rewrite

import emotion.react.css
import hk.com.vr360_demo2.webapp.component.baseview.DefaultScreenUI
import hk.com.vr360_demo2.webapp.component.common.toggleFullscreen
import hk.com.vr360_demo2.webapp.component.editor_rewrite.component.EditorBottomSceneSwitchBar
import hk.com.vr360_demo2.webapp.component.editor_rewrite.component.CreateHotspotDialog
import hk.com.vr360_demo2.webapp.component.editor_rewrite.component.EditorHotspotListPanel
import hk.com.vr360_demo2.webapp.component.editor_rewrite.component.EditorInitialViewPanel
import hk.com.vr360_demo2.webapp.component.editor_rewrite.component.EditorRightActionPanel
import hk.com.vr360_demo2.webapp.component.marzipano.EditorPanoramaViewer
import hk.com.vr360_demo2.webapp.component.marzipano.EditorViewerHandle
import hk.com.vr360_demo2.webapp.component.marzipano.HotspotCreateOptions
import hk.com.vr360_demo2.webapp.component.marzipano.PanoramaLinkHotspot
import hk.com.vr360_demo2.webapp.core.ui_state.ProgressBarState
import react.FC
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.img
import react.useEffect
import react.useRef
import react.useState
import kotlinx.browser.window
import web.cssom.Background
import web.cssom.Cursor
import web.cssom.Position
import web.cssom.ZIndex
import web.cssom.pct
import web.cssom.px
import kotlin.js.unsafeCast

external interface EditorRewriteProps : Props {
    var component: EditorRewriteComponent
}

private data class PendingHotspotDraft(
    val sceneId: String,
    val hotspotId: String,
    val mode: PendingDraftMode,
    val originalHotspot: PanoramaLinkHotspot? = null,
)

private enum class PendingDraftMode {
    Create,
    Edit,
}

private const val ACTION_INITIAL_VIEW = "初始視角"
private const val ACTION_HOTSPOT = "熱點"
private const val ACTION_PREVIEW = "預覽"

val EditorRewriteScreen = FC<EditorRewriteProps> { props ->
    val (uiState, setUiState) = useState(EditorRewriteState())
    val (activeAction, setActiveAction) = useState<String?>(null)
    val (initialViewImage, setInitialViewImage) = useState<String?>(null)
    val (currentSceneHotspots, setCurrentSceneHotspots) = useState<List<PanoramaLinkHotspot>>(emptyList())
    val (createHotspotDialogOpen, setCreateHotspotDialogOpen) = useState(false)
    val (editingHotspot, setEditingHotspot) = useState<PanoramaLinkHotspot?>(null)
    val (pendingHotspotDraft, setPendingHotspotDraft) = useState<PendingHotspotDraft?>(null)
    val viewerHandleRef = useRef<EditorViewerHandle>()

    useEffect {
        props.component.state.collect {
            setUiState(it)
        }
    }

    useEffect(uiState.activeSceneId, uiState.scenes) {
        val sceneId = uiState.activeSceneId
        if (sceneId == null) {
            setCurrentSceneHotspots(emptyList())
            return@useEffect
        }
        val hotspots = viewerHandleRef.current?.getSceneHotspots?.invoke(sceneId)
            ?: uiState.scenes.find { it.id == sceneId }?.linkHotspots.orEmpty()
        setCurrentSceneHotspots(hotspots)
    }

    fun captureAndUpdateInitialViewImage() {
        val snapshot = viewerHandleRef.current?.captureView?.invoke()?.snapshot
        setInitialViewImage(snapshot)
    }

    useEffect(activeAction, uiState.activeSceneId) {
        if (activeAction == ACTION_INITIAL_VIEW) {
            window.setTimeout(
                {
                    captureAndUpdateInitialViewImage()
                },
                400,
            )
        }
    }

    val hasReady = uiState.scenes.isNotEmpty()

    DefaultScreenUI {
        this.hasReady = hasReady
        this.progressBarState = uiState.progressBarState
        this.networkState = uiState.networkState
        this.errors = props.component.errors
        this.emptyMessage = "暫無場景數據"
        this.onTryAgain = { props.component.dispatch(EditorRewriteIntent.Refresh) }

        if (!hasReady) return@DefaultScreenUI

        div {
            css {
                position = Position.relative
                width = 100.pct
                height = 100.pct
            }

            EditorPanoramaViewer {
                this.scenes = uiState.scenes
                this.activeSceneId = uiState.activeSceneId
                this.onSceneChange = { sceneId ->
                    props.component.dispatch(EditorRewriteIntent.SwitchScene(sceneId))
                }
                this.onHandleReady = { handle ->
                    viewerHandleRef.current = handle
                    val activeSceneId = uiState.activeSceneId
                    if (activeSceneId != null) {
                        setCurrentSceneHotspots(handle.getSceneHotspots(activeSceneId))
                    }
                }
                this.onHotspotsChange = { sceneId, hotspots ->
                    if (sceneId == uiState.activeSceneId) {
                        setCurrentSceneHotspots(hotspots)
                    }
                }
                this.pendingDraftSceneId = pendingHotspotDraft?.sceneId
                this.pendingDraftHotspotId = pendingHotspotDraft?.hotspotId
                this.onConfirmPendingDraft = {
                    val draft = pendingHotspotDraft
                    val handle = viewerHandleRef.current
                    if (draft != null && handle != null) {
                        val pendingDraftHotspot = handle.getPendingDraftHotspotById(
                            draft.sceneId,
                            draft.hotspotId,
                        )
                        val hotspotsForSave = if (pendingDraftHotspot != null && draft.mode == PendingDraftMode.Edit) {
                            currentSceneHotspots.map {
                                if (it.hotspotId == draft.hotspotId) pendingDraftHotspot else it
                            }
                        } else if (pendingDraftHotspot != null) {
                            currentSceneHotspots + pendingDraftHotspot
                        } else {
                            currentSceneHotspots
                        }
                        props.component.saveCurrentHotspotsMock(
                            sceneId = draft.sceneId,
                            hotspots = hotspotsForSave,
                            onSuccess = {
                                viewerHandleRef.current?.confirmPendingDraftHotspotById?.invoke(
                                    draft.sceneId,
                                    draft.hotspotId,
                                )
                                setPendingHotspotDraft(null)
                                setEditingHotspot(null)
                            },
                        )
                    }
                }
                this.onCancelPendingDraft = {
                    val draft = pendingHotspotDraft
                    if (draft != null) {
                        if (draft.mode == PendingDraftMode.Edit && draft.originalHotspot != null) {
                            viewerHandleRef.current?.restoreHotspotById?.invoke(
                                draft.sceneId,
                                draft.hotspotId,
                                draft.originalHotspot,
                            )
                        } else {
                            viewerHandleRef.current?.removeHotspotById?.invoke(draft.sceneId, draft.hotspotId)
                        }
                        setPendingHotspotDraft(null)
                        setEditingHotspot(null)
                    }
                }
            }

            button {
                title = "全屏"
                onClick = { toggleFullscreen() }
                css {
                    position = Position.fixed
                    left = 16.px
                    top = 16.px
                    zIndex = 50.asDynamic().unsafeCast<ZIndex>()
                    asDynamic().pointerEvents = "auto"
                    background = "none".unsafeCast<Background>()
                    border = 0.px
                    cursor = Cursor.pointer
                    padding = 8.px
                    asDynamic().outline = "none"
                }
                img {
                    src = "/assets/images/full.svg"
                    alt = "全屏"
                    css {
                        width = 36.px
                        height = 36.px
                        cursor = Cursor.pointer
                        asDynamic().transition = "transform 0.2s ease"
                        hover {
                            asDynamic().transform = "scale(1.2)"
                        }
                        asDynamic()["@media (max-width: 600px)"] = js("{}").unsafeCast<react.CSSProperties>().apply {
                            width = 24.px
                            height = 24.px
                        }
                    }
                }
            }

            EditorBottomSceneSwitchBar {
                this.scenes = uiState.scenes
                this.currentSceneId = uiState.activeSceneId
                this.onSwitchScene = { sceneId ->
                    props.component.dispatch(EditorRewriteIntent.SwitchScene(sceneId))
                }
            }

            EditorRightActionPanel {
                this.activeAction = activeAction
                onActionClick = {
                    when (it) {
                        ACTION_PREVIEW -> {
                            val uid = uiState.uid?.takeIf { it.isNotBlank() }
                            val viewerUrl = if (uid != null) {
                                "/view/${props.component.id}?uid=$uid"
                            } else {
                                "/view/${props.component.id}"
                            }
                            window.open(viewerUrl, "_blank")
                        }
                        ACTION_INITIAL_VIEW,
                        ACTION_HOTSPOT -> {
                            setActiveAction(if (activeAction == it) null else it)
                        }
                        else -> {
                            // Non-panel actions only emit click behavior and never stay selected.
                            setActiveAction(null)
                        }
                    }
                }
            }

            if (activeAction == ACTION_INITIAL_VIEW) {
                EditorInitialViewPanel {
                    visible = true
                    previewImage = initialViewImage
                    onConfirm = {
                        val sceneId = uiState.activeSceneId
                        val captureResult = viewerHandleRef.current?.captureView?.invoke()
                        if (sceneId != null && captureResult != null) {
                            props.component.saveCurrentInitialViewMock(
                                sceneId = sceneId,
                                initialViewParameters = captureResult.viewParameters,
                                onSuccess = {
                                    setInitialViewImage(captureResult.snapshot)
                                    setActiveAction(null)
                                },
                            )
                        }
                    }
                }
            }

            if (activeAction == ACTION_HOTSPOT) {
                EditorHotspotListPanel {
                    currentSceneId = uiState.activeSceneId
                    scenes = uiState.scenes
                    hotspots = currentSceneHotspots
                    onCreateHotspot = {
                        setEditingHotspot(null)
                        setCreateHotspotDialogOpen(true)
                    }
                    onFocusHotspot = { hotspot ->
                        viewerHandleRef.current?.lookAt?.invoke(hotspot.yaw, hotspot.pitch)
                    }
                    onEditHotspot = { hotspot ->
                        if (pendingHotspotDraft == null) {
                            viewerHandleRef.current?.lookAt?.invoke(hotspot.yaw, hotspot.pitch)
                            setEditingHotspot(hotspot)
                            setCreateHotspotDialogOpen(true)
                        }
                    }
                    onDeleteHotspot = { hotspot ->
                        viewerHandleRef.current?.lookAt?.invoke(hotspot.yaw, hotspot.pitch)
                        val sceneId = uiState.activeSceneId
                        val hotspotId = hotspot.hotspotId
                        if (sceneId != null && hotspotId != null) {
                            val hotspotsForSave = currentSceneHotspots.filter {
                                it.hotspotId != hotspotId
                            }
                            props.component.dispatch(
                                EditorRewriteIntent.ConfirmDeleteHotspotMock(
                                    sceneId = sceneId,
                                    hotspotsForSave = hotspotsForSave,
                                    onSuccess = {
                                        viewerHandleRef.current?.removeHotspotById?.invoke(
                                            sceneId,
                                            hotspotId,
                                        )
                                        setCurrentSceneHotspots(hotspotsForSave)
                                    },
                                ),
                            )
                        }
                    }
                    onMoveHotspot = { hotspot ->
                        if (pendingHotspotDraft == null) {
                            val sceneId = uiState.activeSceneId
                            val hotspotId = hotspot.hotspotId
                            val handle = viewerHandleRef.current
                            if (sceneId != null && hotspotId != null && handle != null) {
                                handle.lookAt(hotspot.yaw, hotspot.pitch)
                                val started = handle.beginMoveHotspotById(sceneId, hotspotId)
                                if (started) {
                                    setPendingHotspotDraft(
                                        PendingHotspotDraft(
                                            sceneId = sceneId,
                                            hotspotId = hotspotId,
                                            mode = PendingDraftMode.Edit,
                                            originalHotspot = hotspot,
                                        ),
                                    )
                                }
                            }
                        }
                    }
                }
            }

            CreateHotspotDialog {
                open = createHotspotDialogOpen
                currentSceneId = uiState.activeSceneId
                scenes = uiState.scenes
                dialogTitle = if (editingHotspot != null) "編輯熱點" else "新增熱點"
                initialTargetSceneId = editingHotspot?.target
                initialIconUrl = editingHotspot?.imageName
                onClose = { setCreateHotspotDialogOpen(false) }
                onConfirm = { targetSceneId, iconUrl ->
                    val activeSceneId = uiState.activeSceneId
                    val handle = viewerHandleRef.current
                    if (activeSceneId == null || handle == null) {
                        setCreateHotspotDialogOpen(false)
                    } else {
                        val editing = editingHotspot
                        val hotspotId = if (editing?.hotspotId != null) {
                            val ok = handle.beginEditHotspotById(
                                activeSceneId,
                                editing.hotspotId!!,
                                HotspotCreateOptions(
                                    targetSceneId = targetSceneId,
                                    iconUrl = iconUrl,
                                ),
                            )
                            if (ok) editing.hotspotId else null
                        } else {
                            handle.addHotspotWithOptions.invoke(
                                HotspotCreateOptions(
                                    targetSceneId = targetSceneId,
                                    iconUrl = iconUrl,
                                ))
                        }

                        if (hotspotId != null && activeSceneId != null) {
                            setPendingHotspotDraft(
                                PendingHotspotDraft(
                                    sceneId = activeSceneId,
                                    hotspotId = hotspotId,
                                    mode = if (editing != null) PendingDraftMode.Edit else PendingDraftMode.Create,
                                    originalHotspot = editing,
                                )
                            )
                        }
                    }
                    setCreateHotspotDialogOpen(false)
                }
            }

        }
    }
}
